import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';
import { useMemo } from 'react';
import { z } from 'zod';

import { InfoPage } from '@/components/info-page';
import { SchoolNameDivider } from '@/components/students/school-name-divider';
import { StudentBulkUpdate } from '@/components/students/student-bulk-update';
import { StudentStatusBadge } from '@/components/students/student-status-badge';
import { EntityTable, EntityTableGroupBy, createTableRowSelectionCell } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { EmptyArray, getStudents } from '@/lib/api-calls';
import { usePaginatedFilters } from '@/lib/filtering';
import { DEFAULT_PAGE_SIZE, paginationRequestSchema } from '@/lib/pagination';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing';
import { getMostRecentSession, isAirAdmin } from '@airreading/common';
import { LearningPathNames } from '@airreading/common/data';

import { useAllSchools, useOrgOptions, useSchoolAndOrgOptions } from '@/lib/api-call-hooks';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { getDistrictColumn } from '@/lib/district-utils';
import { useErrorToast } from '@/lib/error-handling';
import {
  calculateAttendanceRate,
  getStudentYearOptions,
  getTotalFutureSessionsFilterOptions,
} from '@/lib/student-utils';
import { SchoolNameGroupKey, groupBySchoolName } from '@/lib/table-grouping-utils';
import { airColumnHelper } from '@/lib/tables';

import { useTimeZone } from '@/auth/useTimeZone';
import { useSorting } from '@/lib/sorting';
import { CreateStudent } from '@/routes/_layout/users/-components/create-student';
import { CreateStudentsByCsv } from '@/routes/_layout/users/-components/create-students-by-csv';
import { formatInstant } from '@airreading/common';
import {
  FullStudent,
  GetStudentsSortableColumn,
  StudentStatus,
  StudentYearColorMap,
  getStudentsSortOrderSchema,
  getStudentsSortableColumnSchema,
  studentStatusSchema,
  studentYearOption,
  studentYearSchema,
} from '@airreading/common/data';
import { studentsTabs } from '../-components/tabs';

const studentsSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  schools: z.array(z.string()).optional(),
  orgs: z.array(z.string()).optional(),
  grades: z.array(studentYearSchema).optional(),
  statuses: z.array(studentStatusSchema).optional(),
  totalFutureSessions: z.array(z.number()).optional(),
  sortBy: getStudentsSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/students/no-future-sessions/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: StudentsIndex,
  validateSearch: studentsSearchSchema,
});

const col = airColumnHelper<FullStudent>(getStudentsSortableColumnSchema);

const buildColumns = (timeZone: string, schoolIdToDistrictName: Record<string, string>) => [
  createTableRowSelectionCell(col),
  col.accessor((row) => row.id, {
    id: 'fullNameOrEmail',
    header: 'Student Name',
    cell: ({ row, getValue }) => (
      <Link
        to={'/students/$studentId'}
        params={{ studentId: getValue() }}
        search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
      >
        {row.original.fullNameOrEmail}
      </Link>
    ),
  }),
  col.accessor('email', {
    id: 'email',
    header: 'Email',
  }),
  col.accessor('school', {
    id: 'district',
    header: 'District',
    cell: ({ getValue }) => {
      const school = getValue();
      const districtName = school?.id ? schoolIdToDistrictName[school.id] : null;
      if (!districtName) return <span>Unknown</span>;
      return school
        ? getDistrictColumn({
            orgId: school.orgId,
            orgName: districtName,
          })
        : null;
    },
  }),
  col.accessor((row) => row, {
    id: 'school',
    header: 'School',
    cell: ({ getValue }) => {
      const school = getValue()?.school;
      return (
        school && (
          <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
            {school.name}
          </Link>
        )
      );
    },
  }),
  col.accessor('grade', {
    id: 'grade',
    header: 'Grade',
    cell: ({ getValue }) => {
      const grade = getValue();
      if (!grade) return studentYearOption(grade);
      const backgroundColor = StudentYearColorMap[grade];
      return (
        <div className="min-w-[80px] flex justify-start">
          <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
            {studentYearOption(grade)}
          </span>
        </div>
      );
    },
  }),
  col.accessor('status', {
    id: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      return (
        <div className="flex gap-x-1">
          <StudentStatusBadge status={getValue()} />
        </div>
      );
    },
  }),
  col.accessor('studentSessions', {
    id: 'mostRecentSessionDate',
    header: 'Most recent session date',
    cell: ({ getValue }) => {
      const latestSession = getMostRecentSession(getValue() ?? [], timeZone);
      return latestSession && formatInstant(latestSession.startInstant, timeZone).formattedDate;
    },
  }),
  col.accessor('learningPath', {
    id: 'learning-path',
    header: 'Learning Path',
    cell: ({ getValue }) => {
      const learningPath = getValue();

      return learningPath?.name;
    },
  }),
  col.accessor('startingLesson', {
    id: 'starting-lesson',
    header: 'Starting lesson',
    cell: ({ getValue }) => {
      const startingLesson = getValue();
      return startingLesson?.name;
    },
  }),
  col.accessor('studentSessions', {
    id: 'attendance-all-past-sessions-last-14-days',
    header: 'Attendance - past 14 days',
    cell: ({ row }) => {
      const sessionFeedbacks = row.original.sessionFeedbacks;
      return `${calculateAttendanceRate(sessionFeedbacks, 14)}%`;
    },
  }),
  col.accessor('totalFutureSessions', {
    id: 'totalFutureSessions',
    header: 'Total future sessions',
    cell: ({ getValue }) => {
      const count = getValue();
      return count ?? 0;
    },
  }),
];

function StudentsIndex() {
  const { timeZone } = useTimeZone();
  const { data: allSchools } = useAllSchools();

  const schoolIdToDistrictName = useMemo(
    () => Object.fromEntries(allSchools.map((school) => [school.id, school.org.name])),
    [allSchools],
  );

  const columns = useMemo(() => buildColumns(timeZone, schoolIdToDistrictName), [timeZone, schoolIdToDistrictName]);
  const [sortByFromSearch, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  // Default sort for this table: by school name ascending
  const effectiveSortBy = sortByFromSearch ?? ['school-asc'];
  const { sorting, ...tableSortingOptions } = useSorting<GetStudentsSortableColumn>([effectiveSortBy, didChangeSortBy]);

  const isSortingBySchool = sorting.some((s) => s.id === 'school');

  const schoolGroupBy = useMemo<EntityTableGroupBy<FullStudent, SchoolNameGroupKey> | undefined>(
    () =>
      isSortingBySchool
        ? {
            getGroupKey: (row) => groupBySchoolName(row.school?.name),
            renderDivider: (groupKey, rowCount) => <SchoolNameDivider groupKey={groupKey} rowCount={rowCount} />,
          }
        : undefined,
    [isSortingBySchool],
  );

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[userNameQuery, activeUserNameQuery], setUserNameQuery],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      orgs: [[selectedOrgs, activeSelectedOrgs], setSelectedOrgs],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      totalFutureSessions: [
        [selectedTotalFutureSessions, activeSelectedTotalFutureSessions],
        setSelectedTotalFutureSessions,
      ],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'schools', 'orgs', 'grades', 'totalFutureSessions']);

  const allOrganizationOptions = useOrgOptions();
  const allSchoolOptions = useSchoolAndOrgOptions({
    orgIds: activeSelectedOrgs ?? [],
  });

  const allowedLearningPathNames = useMemo(
    () => [
      LearningPathNames.Regular,
      LearningPathNames['Regular - extended'],
      LearningPathNames.Beginner,
      LearningPathNames['Beginner - extended'],
      LearningPathNames['Middle School'],
      LearningPathNames.Fluency,
      LearningPathNames['Makeup Diagnostic'],
      LearningPathNames['Read Aloud'],
      'Practice',
      LearningPathNames.Diagnostic,
      'Diagnostic w/ Practice',
    ],
    [],
  );

  const {
    data: response,
    isPending,
    error,
    refetch,
  } = useQuery(
    getStudents({
      pageIndex,
      pageSize,
      filters: {
        query: activeUserNameQuery,
        schools: activeSelectedSchools,
        orgs: activeSelectedOrgs,
        grades: activeSelectedGrades,
        statuses: ['enrolled'],
        totalFutureSessions:
          activeSelectedTotalFutureSessions && activeSelectedTotalFutureSessions.length > 0
            ? activeSelectedTotalFutureSessions
            : [0, 1], // Default to max 1 future session
        excludeTest: true,
        hasPastSessions: true,
        learningPathNames: allowedLearningPathNames,
        scheduleEndDateAfterToday: true,
      },
      sortBy: effectiveSortBy,
    }),
  );

  const students = useMemo(() => response?.students || EmptyArray, [response]);
  useErrorToast(error, 'Error loading students. Please try again.');

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<FullStudent>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => students.find((student) => student.id === id),
    });

  const onSuccess = () => {
    resetAllRowSelection();
    refetch();
  };

  const table = useReactTable({
    data: students,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
      rowSelection,
    },
    rowCount: response?.total || 0,
    onRowSelectionChange,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    ...tableSortingOptions,
    enableColumnResizing: true,
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search users by name or email"
          value={userNameQuery ?? ''}
          onChange={(event) => {
            setUserNameQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="Districts"
          options={allOrganizationOptions}
          selectedValues={selectedOrgs ?? []}
          onChange={setSelectedOrgs}
        />
        <TableFilterDropdown
          title="Schools"
          options={allSchoolOptions}
          selectedValues={selectedSchools ?? []}
          onChange={setSelectedSchools}
        />
        <TableFilterDropdown
          title="Grades"
          options={getStudentYearOptions()}
          selectedValues={selectedGrades ?? []}
          onChange={setSelectedGrades}
        />
        <TableFilterDropdown
          title="Total future sessions"
          options={getTotalFutureSessionsFilterOptions()}
          selectedValues={selectedTotalFutureSessions ?? []}
          onChange={setSelectedTotalFutureSessions}
        />

        <TableFilterResetButton show={true} onClick={resetFilters} />
      </>
    ),
    [
      userNameQuery,
      setUserNameQuery,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      allOrganizationOptions,
      selectedOrgs,
      setSelectedOrgs,
      selectedGrades,
      setSelectedGrades,
      selectedTotalFutureSessions,
      setSelectedTotalFutureSessions,
      resetFilters,
    ],
  );

  const bulkActionsMenuItems = (
    <>
      <StudentBulkUpdate
        confirmMessage="Are you sure you want to drop"
        invalidMessage="Only enrolled students can be selected."
        options={{ status: StudentStatus.dropped }}
        onSuccess={onSuccess}
        students={rowDataSelection}
        title="Mark drop off"
        validation={(student) => student.status === StudentStatus.enrolled}
      />
      <StudentBulkUpdate
        confirmMessage="Are you sure you want to conclude the program for"
        invalidMessage="Only students with no program concluded status can be selected."
        options={{ status: StudentStatus.program_concluded }}
        onSuccess={onSuccess}
        students={rowDataSelection}
        title="Mark program concluded"
        validation={(student) => student.status !== StudentStatus.program_concluded}
      />
    </>
  );

  const topBarRightComponents = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button className="ml-auto hidden h-8 lg:flex">
          Add students
          <ChevronDown className="mr-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[150px]">
        <CreateStudent
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Add a student</DropdownMenuItem>}
        />
        <CreateStudentsByCsv
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Import from CSV</DropdownMenuItem>}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <InfoPage headerText="Students" tabs={studentsTabs('No Future session')}>
      <EntityTable
        table={table}
        showPagination={true}
        isLoading={isPending}
        hasError={!!error}
        emptyState={'No students found'}
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents }}
        bulkActionsMenuItems={bulkActionsMenuItems}
        topBarRightComponents={topBarRightComponents}
        groupBy={schoolGroupBy}
      />
    </InfoPage>
  );
}
